import { useRef } from "react";
import { useTranslation } from "react-i18next";

/**
 * 6 haneli OTP giriş grid'i — auto-advance, backspace-geri, tam kod yapıştırma.
 *
 * Hem login (auth.tsx) hem kayıt (AuthRegisterForm) OTP adımı bunu kullanır →
 * tek stil, tek davranış. Koyu (dark) tema: auth sayfasının paletiyle.
 *
 * Kontrollü bileşen: `digits` dışarıda tutulur, her değişiklik `onChange` ile
 * yukarı bildirilir. Referanslar içeride yönetilir; `autoFocus` ilk kutuyu
 * mount olduğunda odaklar (OTP adımı görünür olduğunda mount olur).
 */
interface OtpDigitsProps {
  digits: string[];
  onChange: (next: string[]) => void;
  autoFocus?: boolean;
  /** aria-label kökü — verilmezse `auth.codeLabel` kullanılır. */
  ariaLabel?: string;
}

export function OtpDigits({ digits, onChange, autoFocus = false, ariaLabel }: OtpDigitsProps) {
  const { t } = useTranslation();
  const refs = useRef<(HTMLInputElement | null)[]>(Array(digits.length).fill(null));
  const label = ariaLabel ?? t("auth.codeLabel");

  function handleChange(index: number, value: string) {
    const clean = value.replace(/\D/g, "");
    if (!clean) return;
    const next = [...digits];
    next[index] = clean[clean.length - 1];
    onChange(next);
    if (index < digits.length - 1) refs.current[index + 1]?.focus();
  }

  function handleKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace") {
      if (digits[index]) {
        const next = [...digits];
        next[index] = "";
        onChange(next);
        if (index > 0) refs.current[index - 1]?.focus();
      } else if (index > 0) {
        refs.current[index - 1]?.focus();
      }
    }
  }

  function handlePaste(e: React.ClipboardEvent) {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, digits.length);
    const next = Array(digits.length).fill("");
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    onChange(next);
    refs.current[Math.min(pasted.length, digits.length - 1)]?.focus();
  }

  return (
    <div
      className="grid grid-cols-6 gap-2"
      role="group"
      aria-label={label}
      onPaste={handlePaste}
    >
      {digits.map((digit, i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          pattern="\d*"
          maxLength={1}
          autoComplete={i === 0 ? "one-time-code" : "off"}
          autoFocus={autoFocus && i === 0}
          value={digit}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          aria-label={`${label} ${i + 1}`}
          className="w-full h-14 bg-white/5 border border-primary/20 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded-lg text-center text-2xl font-bold text-white transition-[border-color,box-shadow]"
        />
      ))}
    </div>
  );
}
